import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Wrench, ChevronRight, X, MessageCircle, Send, ArrowLeft, CheckCircle2, PhoneIncoming, Plus } from 'lucide-react';
import QuickBookingModal from '@/components/QuickBookingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const isManager = (role) => ['super_admin', 'admin', 'service_manager', 'floor_manager'].includes(role);
const isMechanic = (role) => role === 'mechanic';

/**
 * Always-visible floating widget (bottom-right) shown on every page.
 *  - Managers: pending-jobs alert + workshop chat inbox (one row per mechanic).
 *  - Mechanics: single chat thread with their managers, plus unread badge.
 * Both sides share the same compact panel so communication is one click away
 * without leaving the current page.
 */
export default function FloatingOpsWidget({ user, chatEnabled = true }) {
  const [pending, setPending] = useState(null);
  // iter138.9aa — unified direct-chat contacts (mechanics + SM + FM + SA).
  // Replaces the legacy mechanic-only threads UI. Each contact carries
  // last_message_at, last_message_text, unread.
  const [contacts, setContacts] = useState([]);
  const [activePeerId, setActivePeerId] = useState(null);
  const [activePeerName, setActivePeerName] = useState(null);
  const [activePeerRole, setActivePeerRole] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    // Remember the user's last choice across navigations. Default to collapsed
    // so the widget never overlaps content unless the user expands it OR an
    // unread message arrives (handled by the auto-expand effect below).
    try { return localStorage.getItem('opsWidgetCollapsed') !== 'false'; }
    catch { return true; }
  });
  // iter139.6 — Removed the legacy `dismissed` "hide forever" state.
  // Previously the X (close) button in the expanded header set
  // dismissed=true which short-circuited the render to `return null`,
  // taking away the Booking pill, Pending pill AND the Pulse pill. SM
  // wants the X to merely COLLAPSE the panel back to the pills (same
  // as the Minus button) — the pills must always stay visible.
  // Track the unread count we've ALREADY auto-expanded for so we don't
  // re-pop the widget after the SM has manually collapsed it again.
  const lastAutoExpandRef = useRef(0);
  const [showQuickBooking, setShowQuickBooking] = useState(false);
  // iter138.9y — SA-only broadcast composer state. When opened, the SA
  // types one message that fans out to every mechanic thread at the site.
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastDraft, setBroadcastDraft] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const navigate = useNavigate();
  const scrollerRef = useRef(null);

  const manager = isManager(user?.role);
  const mechanic = isMechanic(user?.role);
  const allowed = manager || mechanic;

  const loadInbox = useCallback(async () => {
    if (!allowed) return;
    try {
      // iter138.9aa — fetch contacts directory (all chat-visible users at
      // the site) plus pending-jobs for managers. Replaces the legacy
      // workshop-chat threads + my-thread split.
      const requests = [
        fetch(`${BACKEND_URL}/api/direct-chat/contacts`, { credentials: 'include' }),
      ];
      if (manager) {
        requests.push(fetch(`${BACKEND_URL}/api/job-cards/pending-from-previous-weeks`, { credentials: 'include' }));
      }
      const [cRes, pRes] = await Promise.all(requests);
      if (cRes?.ok) {
        const data = await cRes.json();
        setContacts(data.contacts || []);
      }
      if (manager && pRes?.ok) {
        setPending(await pRes.json());
      }
    } catch (_e) { /* noop */ }
  }, [allowed, manager]);

  // Periodically refresh inbox + pending jobs
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    const tick = async () => { if (!cancelled) await loadInbox(); };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [allowed, loadInbox]);

  // Load active thread's messages (polls every 5s while open) — iter138.9aa
  // uses the unified direct-chat endpoint that works for any peer.
  const loadMessages = useCallback(async (peerId) => {
    if (!peerId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/direct-chat/threads/${peerId}/messages`, { credentials: 'include' });
      if (res?.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (_e) { /* noop */ }
  }, []);

  useEffect(() => {
    if (!activePeerId) return;
    loadMessages(activePeerId);
    const id = setInterval(() => loadMessages(activePeerId), 5000);
    return () => clearInterval(id);
  }, [activePeerId, loadMessages]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, activePeerId]);

  // Auto-expand only when a NEW unread message arrives. Once the user
  // collapses, we won't re-expand for the same unread total — they have to
  // explicitly click the pill to read it.
  useEffect(() => {
    const total = (contacts || []).reduce((acc, c) => acc + (c.unread || 0), 0);
    if (total > lastAutoExpandRef.current) {
      lastAutoExpandRef.current = total;
      if (collapsed) {
        setCollapsed(false);
        try { localStorage.setItem('opsWidgetCollapsed', 'false'); } catch (_e) {/* noop */}
      }
    } else if (total === 0) {
      // Reset the reference once all unreads are cleared
      lastAutoExpandRef.current = 0;
    }
     
  }, [contacts]);

  // Persist user's collapse choice
  useEffect(() => {
    try { localStorage.setItem('opsWidgetCollapsed', collapsed ? 'true' : 'false'); }
    catch (_e) {/* noop */}
  }, [collapsed]);

  const openPeer = (peerId, peerName, peerRole) => {
    setActivePeerId(peerId);
    setActivePeerName(peerName);
    setActivePeerRole(peerRole);
    setDraft('');
  };

  const closeChat = () => {
    setActivePeerId(null);
    setActivePeerName(null);
    setActivePeerRole(null);
    setMessages([]);
    loadInbox();
  };

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending || !activePeerId) return;
    setSending(true);
    try {
      // iter138.9aa — Unified direct-chat send. Works for any peer.
      const res = await fetch(
        `${BACKEND_URL}/api/direct-chat/threads/${activePeerId}/messages`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        }
      );
      if (res?.ok) {
        setDraft('');
        await loadMessages(activePeerId);
      }
    } finally {
      setSending(false);
    }
  };

  // iter138.9y — SA broadcast handler. Fans out to every mechanic thread.
  const sendBroadcast = async (e) => {
    e?.preventDefault?.();
    const text = broadcastDraft.trim();
    if (!text || broadcasting) return;
    setBroadcasting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workshop-chat/broadcast`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (res?.ok) {
        setBroadcastDraft('');
        setShowBroadcast(false);
        await loadInbox();
      }
    } finally {
      setBroadcasting(false);
    }
  };

  // iter138.9z — SA manual trigger for the daily digest SMS to themselves.
  // Cron also fires this at 6pm Melbourne; this button is the override
  // ("Send today's digest now") and a test-fire mechanism.
  const [sendingDigest, setSendingDigest] = useState(false);
  const sendDailyDigest = async () => {
    if (sendingDigest) return;
    setSendingDigest(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/daily-digest/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res?.ok) {
        const data = await res.json();
        const recip = (data?.recipients || []).filter(r => r.status === 'sent' || r.status === 'dry_run').length;
        const total = (data?.recipients || []).length;
        // Use the existing console for diagnostics; success message via window.alert
        // is intentionally minimal — full UX would be a toast, but the
        // FloatingOpsWidget intentionally doesn't pull in a toast lib.
         
        window.alert(
          total === 0
            ? "No Super Admin with a mobile on file. Add your mobile under Users → Edit → Mobile to receive the digest."
            : `Daily digest sent to ${recip}/${total} SA recipient${total === 1 ? '' : 's'}.`
        );
      }
    } finally {
      setSendingDigest(false);
    }
  };

  if (!allowed) return null;

  // ---- header data ------------------------------------------------------
  // iter138.9aa — Unread = sum of unread across the unified contacts list.
  const totalUnread = (contacts || []).reduce((acc, c) => acc + (c.unread || 0), 0);

  const hasUrgent = manager && ((pending?.critical || 0) > 0 || (pending?.total || 0) > 0);
  const hasUnread = totalUnread > 0;
  // ---- collapsed pill ---------------------------------------------------
  // iter239 — when the workshop's subscription lacks Pulse Chat, only the
  // Booking + Pending pills render (chat pill + panel hidden entirely).
  if (collapsed || !chatEnabled) {
    return (
      <div className="fixed bottom-4 right-4 z-40 flex items-center gap-2" data-testid="ops-widget">
        {/* Quick-booking pill (managers only) */}
        {manager && (
          <button
            onClick={() => setShowQuickBooking(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full shadow-lg border bg-white text-slate-700 border-slate-200 hover:bg-slate-50 text-xs font-semibold"
            data-testid="ops-widget-quick-booking"
            title="Take a phone-call booking"
          >
            <Plus className="h-3.5 w-3.5" /> Booking
          </button>
        )}
        {/* Pending-jobs pill (managers, only when there are any) */}
        {manager && hasUrgent && (
          <button
            onClick={() => navigate('/job-cards?pending_prev_weeks=1')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full shadow-lg border bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 text-xs font-semibold"
            data-testid="ops-widget-pending"
            title="Open pending-jobs kanban"
          >
            <AlertTriangle className="h-3.5 w-3.5" /> {pending?.total || 0} pending
          </button>
        )}
        {/* Main pulse pill — auto-pulses red when there are unread messages */}
        {chatEnabled && (
        <button
          onClick={() => setCollapsed(false)}
          className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border text-xs font-semibold ${
            hasUnread
              ? 'bg-rose-600 text-white border-rose-700 animate-pulse'
              : 'bg-slate-900 text-white border-slate-700 hover:bg-slate-800'
          }`}
          data-testid="ops-widget-collapsed"
        >
          {hasUnread
            ? <><MessageCircle className="h-4 w-4" />{totalUnread} unread</>
            : <><Wrench className="h-4 w-4" />Workshop Pulse</>}
        </button>
        )}
        {/* Quick-booking modal still wired even from the collapsed pill */}
        {showQuickBooking && (
          <QuickBookingModal
            open={showQuickBooking}
            onClose={() => setShowQuickBooking(false)}
            user={user}
          />
        )}
      </div>
    );
  }

  // ---- expanded panel ---------------------------------------------------
  return (
    <div className="fixed bottom-4 right-4 z-40 w-80" data-testid="ops-widget">
      <div className="bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 bg-slate-900 text-white">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
            {activePeerId ? (
              <>
                <button
                  onClick={closeChat}
                  className="p-1 hover:bg-white/10 rounded"
                  data-testid="ops-widget-back"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
                <span className="truncate">{activePeerName || 'Chat'}</span>
                {activePeerRole && (
                  <span className="ml-1 text-[9px] uppercase tracking-wider opacity-60">
                    {activePeerRole.replace('_', ' ')}
                  </span>
                )}
              </>
            ) : (
              <>
                <Wrench className="h-3.5 w-3.5" /> Workshop Pulse
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setCollapsed(true)} className="p-1 hover:bg-white/10 rounded text-xs" data-testid="ops-widget-collapse">
              –
            </button>
            {/* iter139.6 — X (close) now just COLLAPSES the panel back to
                the pills. Previously it dismissed the entire widget
                including the Booking + Pending pills, which the SM
                reported as a bug. The pills must always stay visible. */}
            <button onClick={() => setCollapsed(true)} className="p-1 hover:bg-white/10 rounded" data-testid="ops-widget-dismiss" title="Close panel (pills stay)">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* ---------- CHAT VIEW (active thread) ---------- */}
        {activePeerId ? (
          <div className="flex flex-col h-[420px]">
            <div
              ref={scrollerRef}
              className="flex-1 overflow-y-auto px-3 py-2 space-y-2 bg-slate-50"
              data-testid="ops-widget-messages"
            >
              {messages.length === 0 ? (
                <p className="text-xs text-slate-400 text-center mt-6">
                  No messages yet. Start the conversation below.
                </p>
              ) : (
                messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div
                      key={m.message_id}
                      className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                      data-testid={`ops-widget-message-${m.message_id}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm ${mine ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-800'}`}
                      >
                        {!mine && (
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
                            {m.sender_name || m.sender_role}
                          </p>
                        )}
                        <p className="whitespace-pre-wrap break-words leading-snug">{m.text}</p>
                        <p className={`text-[10px] mt-0.5 ${mine ? 'text-white/60' : 'text-slate-400'}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <form onSubmit={sendMessage} className="flex items-end gap-2 px-3 py-2 border-t border-slate-200 bg-white">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                rows={2}
                placeholder={`Message ${activePeerName || 'this person'}…`}
                className="flex-1 resize-none text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                data-testid="ops-widget-input"
              />
              <button
                type="submit"
                disabled={!draft.trim() || sending}
                className="p-2 rounded-md bg-slate-900 text-white disabled:opacity-40"
                data-testid="ops-widget-send"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        ) : (
          /* ---------- INBOX VIEW ---------- */
          <div className="max-h-[480px] overflow-y-auto">
            {/* Action row: Pending jobs + New Booking (managers only) */}
            {manager && (
              <div className="grid grid-cols-2 border-b border-slate-100">
                <button
                  onClick={() => navigate('/job-cards?pending_prev_weeks=1')}
                  className={`text-left px-3 py-3 border-r border-slate-100 hover:bg-slate-50 transition-colors ${pending?.critical ? 'bg-rose-50/40' : ''}`}
                  data-testid="ops-widget-pending"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <AlertTriangle className={`h-3.5 w-3.5 ${pending?.critical ? 'text-rose-600' : 'text-slate-400'}`} />
                    <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Pending Jobs</span>
                  </div>
                  <p className={`text-2xl font-bold ${pending?.total > 0 ? 'text-slate-900' : 'text-slate-400'}`}>
                    {pending?.total ?? '–'}
                  </p>
                  {pending?.critical > 0 ? (
                    <p className="text-[10px] font-medium text-rose-600 mt-0.5">
                      {pending.critical} crit. &gt;14d
                    </p>
                  ) : (
                    <p className="text-[10px] text-slate-400 mt-0.5">from prev weeks</p>
                  )}
                </button>

                <button
                  onClick={() => setShowQuickBooking(true)}
                  className="text-left px-3 py-3 hover:bg-emerald-50 transition-colors"
                  data-testid="ops-widget-new-booking"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <PhoneIncoming className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">New Booking</span>
                  </div>
                  <p className="text-base font-semibold text-emerald-700 leading-tight">
                    Take a call
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">opens popup · keep working</p>
                </button>
              </div>
            )}

            {/* Chat header */}
            <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  {manager ? 'Mechanic Chat' : 'Chat with Manager'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {/* iter138.9y — SA broadcast button — fans one message
                    out to every mechanic at the site. Only visible for
                    SA / admin. */}
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={() => setShowBroadcast(v => !v)}
                    className="text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded bg-blue-50 border border-blue-300 text-blue-800 hover:bg-blue-100"
                    title="Send a message to every mechanic at this site"
                    data-testid="ops-widget-broadcast-btn"
                  >
                    📣 Broadcast
                  </button>
                )}
                {/* iter138.9z — Manual override for the 6pm Melbourne digest. */}
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={sendDailyDigest}
                    disabled={sendingDigest}
                    className="text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded bg-amber-50 border border-amber-300 text-amber-900 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Send today's mechanic hours + tasks digest to your mobile right now (cron also fires at 6 PM)"
                    data-testid="ops-widget-digest-btn"
                  >
                    {sendingDigest ? '⏳ Sending…' : '📊 Digest'}
                  </button>
                )}
                {totalUnread > 0 && (
                  <span className="text-[10px] font-semibold bg-rose-600 text-white rounded-full px-1.5 py-0.5" data-testid="ops-widget-unread">
                    {totalUnread} new
                  </span>
                )}
              </div>
            </div>

            {/* iter138.9y — Inline SA broadcast composer */}
            {isSuperAdmin && showBroadcast && (
              <form
                onSubmit={sendBroadcast}
                className="px-3 py-2 bg-blue-50/60 border-b border-blue-200"
                data-testid="ops-widget-broadcast-composer"
              >
                <p className="text-[11px] text-blue-900 font-semibold mb-1.5">
                  📣 Announcement — goes to every mechanic at this site
                </p>
                <textarea
                  value={broadcastDraft}
                  onChange={(e) => setBroadcastDraft(e.target.value)}
                  placeholder="e.g. Workshop closing 30 minutes early today for safety training."
                  rows={2}
                  className="w-full text-xs px-2 py-1.5 border border-blue-300 rounded resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="ops-widget-broadcast-text"
                />
                <div className="flex items-center justify-end gap-2 mt-1.5">
                  <button
                    type="button"
                    onClick={() => { setShowBroadcast(false); setBroadcastDraft(''); }}
                    className="text-[11px] text-slate-600 hover:text-slate-900 px-2 py-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!broadcastDraft.trim() || broadcasting}
                    className="text-[11px] font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="ops-widget-broadcast-send"
                  >
                    {broadcasting ? 'Sending…' : 'Send to all'}
                  </button>
                </div>
              </form>
            )}

            {/* iter138.9aa — Unified Contacts list (mechanics + SM + FM
                + SA). SA can hide a user from this directory via the
                User Management → Edit User → "Visible in Pulse Chat"
                toggle (chat_visible field). */}
            <div className="divide-y divide-slate-100" data-testid="ops-widget-contacts-list">
              {contacts.length === 0 ? (
                <p className="text-xs text-slate-400 px-4 py-6 text-center">
                  No chat-visible contacts at this site yet.
                </p>
              ) : (
                contacts.map((c) => (
                  <button
                    key={c.user_id}
                    onClick={() => openPeer(c.user_id, c.name, c.role)}
                    className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-start gap-2"
                    data-testid={`ops-widget-contact-${c.user_id}`}
                  >
                    <div className={`rounded-full h-8 w-8 flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${
                      c.role === 'super_admin'
                        ? 'bg-violet-600 text-white'
                        : c.role === 'service_manager'
                          ? 'bg-blue-600 text-white'
                          : c.role === 'floor_manager'
                            ? 'bg-emerald-600 text-white'
                            : c.role === 'admin'
                              ? 'bg-rose-600 text-white'
                              : 'bg-slate-700 text-white'
                    }`}>
                      {(c.name || '?').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                          <span className={`text-[9px] uppercase tracking-wider px-1 py-0.5 rounded ${
                            c.role === 'super_admin' ? 'bg-violet-50 text-violet-700' :
                            c.role === 'service_manager' ? 'bg-blue-50 text-blue-700' :
                            c.role === 'floor_manager' ? 'bg-emerald-50 text-emerald-700' :
                            c.role === 'admin' ? 'bg-rose-50 text-rose-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {c.role.replace('_', ' ')}
                          </span>
                        </div>
                        {c.last_message_at && (
                          <span className="text-[10px] text-slate-400 whitespace-nowrap">
                            {new Date(c.last_message_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">
                        {c.last_message_text || <span className="italic text-slate-400">No messages yet</span>}
                      </p>
                    </div>
                    {(c.unread || 0) > 0 && (
                      <span className="mt-1 text-[10px] font-semibold bg-rose-600 text-white rounded-full px-1.5 py-0.5">
                        {c.unread}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Non-blocking quick-booking popup — keeps the user's current page intact */}
      <QuickBookingModal
        open={showQuickBooking}
        onClose={() => setShowQuickBooking(false)}
        onCreated={() => { /* booking saved; widget stays open, refresh pending counts */ loadInbox(); }}
      />
    </div>
  );
}
